import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import { title } from '@core/utils/filter'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default function useUsersList() {
  // Use toast
  const toast = useToast()

  const refUserListTable = ref(null)

  // Table Handlers
  const tableColumns = [
    { key: 'name', label: 'User', sortable: true },
    { key: 'email', sortable: true },
    { key: 'org', label: 'Organisation' },
    { key: 'all_analytics_count', label: 'Total views', sortable: true },
    { key: 'last_28_days_analytics_count', label: '1mo views', sortable: true },
    { key: 'last_active', label: 'Last Active', sortable: true },
    { key: 'role', sortable: false },
    { key: 'actions' },
  ]
  const perPage = ref(25)
  const totalUsers = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('name')
  const isSortDirDesc = ref(false)
  const roleFilter = ref(null)
  const planFilter = ref(null)
  const statusFilter = ref(null)
  const requestDone = ref(false)

  const dataMeta = computed(() => {
    const localItemsCount = refUserListTable.value ? refUserListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalUsers.value,
    }
  })

  const refetchData = () => {
    refUserListTable.value.refresh()
  }

  watch([currentPage, perPage, searchQuery, roleFilter, planFilter, statusFilter], () => {
    refetchData()
  })

  const fetchUsers = (ctx, callback) => {
    requestDone.value = false
    store
      .dispatch('app/fetchUsers', {
        q: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        role: roleFilter.value,
        plan: planFilter.value,
        status: statusFilter.value,
      })
      .then(response => {
        const { results, total } = response.data
        callback(results)
        requestDone.value = true
        totalUsers.value = total
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching users list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const fetchOrgs = (ctx, callback) => {
    store
      .dispatch('app' +
        '/fetchOrgs', {
        perPage: 999,
        sortBy: 'name',
      })
      .then(response => {
        callback(response.data.results)
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching organisations list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveUserRoleVariant = role => {
    if (role === 'user') return 'primary'
    if (role === 'manager') return 'warning'
    if (role === 'admin') return 'danger'
    return 'primary'
  }

  const resolveUserRoleIcon = role => {
    if (role === 'user') return 'UserIcon'
    if (role === 'manager') return 'SettingsIcon'
    if (role === 'admin') return 'ServerIcon'
    return 'UserIcon'
  }

  const resolveUserEnabledVariant = status => {
    if (status) return 'success'
    if (!status) return 'secondary'
    return 'secondary'
  }

  const resolveUserStatusVariant = status => {
    if (status === 'full') return 'success'
    if (status === 'inactive') return 'secondary'
    if (status === 'trial') return 'warning'
    if (status === 'expired') return 'secondary'
    return 'secondary'
  }

  const resolveUserStatusText = status => {
    if (status === 'full') return 'Full Access'
    if (status === 'inactive') return 'Inactive'
    if (status === 'trial') return 'Trial'
    if (status === 'expired') return 'Trial Expired'
    return status
  }

  const roleOptions = [
    { label: 'User', value: false },
    { label: 'Manager', value: true },
  ]

  const showOnTeamPage = [
    { label: 'No', value: false },
    { label: 'Yes', value: true },
  ]

  const statusOptions = [
    { label: 'Active', value: true },
    { label: 'Inactive', value: false },
  ]

  return {
    fetchUsers,
    fetchOrgs,
    tableColumns,
    perPage,
    currentPage,
    totalUsers,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refUserListTable,

    resolveUserRoleVariant,
    resolveUserRoleIcon,
    resolveUserEnabledVariant,
    resolveUserStatusVariant,
    resolveUserStatusText,
    roleOptions,
    showOnTeamPage,
    statusOptions,
    refetchData,
    requestDone,

    // Extra Filters
    roleFilter,
    planFilter,
    statusFilter,
  }
}
